import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader, Select } from "../components/DesignSystem";
import type { EventMenuOverview, ID, Menu } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useSubmitGuard } from "./common";

export function MenuScreen() {
  const { workspace, loading, error, service } = usePlannerData();
  const [overview, setOverview] = React.useState<EventMenuOverview[] | null>(null);
  const [overviewLoading, setOverviewLoading] = React.useState(false);
  const [overviewError, setOverviewError] = React.useState<string | null>(null);
  const [editingEvent, setEditingEvent] = React.useState<EventMenuOverview | null>(null);
  const [addingCourseFor, setAddingCourseFor] = React.useState<EventMenuOverview | null>(null);
  const [busyCourseId, setBusyCourseId] = React.useState<ID | null>(null);

  const load = React.useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      setOverview(await service.menu.getOverview());
    } catch (err) {
      setOverviewError(errorMessage(err));
    } finally {
      setOverviewLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading menus" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const removeCourse = async (row: EventMenuOverview, courseId: ID) => {
    if (!row.menu) return;
    setBusyCourseId(courseId);
    try {
      const updatedMenu = await service.menu.removeCourse(row.menu.id, courseId);
      setOverview((prev) => (prev || []).map((item) => (item.eventId === row.eventId ? { ...item, menu: updatedMenu } : item)));
    } catch (err) {
      setOverviewError(errorMessage(err));
    } finally {
      setBusyCourseId(null);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Logistics" title="Food & menu" meta={[{ icon: "utensils", label: "Whole wedding overview" }]} />
      <div className="pob-pad">
        {overviewLoading && !overview && <LoadingState label="Loading menus" />}
        {overviewError && <ErrorState message={overviewError} retry={load} />}

        {overview && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
            {overview.map((row) => {
              const vendor = row.menu?.vendorId ? workspace.vendors.find((v) => v.id === row.menu?.vendorId) : undefined;
              return (
                <Card key={row.eventId} eyebrow={row.eventName} title={row.menu?.title || "No menu set up yet"} action={<Button size="sm" onClick={() => setEditingEvent(row)}>{row.menu ? "Edit" : "Set up menu"}</Button>}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", marginBottom: "var(--space-4)" }}>
                    <Badge tone="neutral">{row.confirmedDiners} confirmed diners</Badge>
                    {vendor && <Badge tone="neutral">Caterer: {vendor.name}</Badge>}
                    {row.menu?.cateringCoverCount != null && <Badge tone="neutral">Catering covers {row.menu.cateringCoverCount}</Badge>}
                    {row.cateringGap > 0 && <Badge tone="error">Catering gap: {row.cateringGap} short</Badge>}
                  </div>

                  {(Object.keys(row.dietarySummary).length > 0 || Object.keys(row.mealPreferenceSummary).length > 0) && (
                    <div style={{ display: "flex", gap: "var(--space-6)", marginBottom: "var(--space-4)", flexWrap: "wrap" }}>
                      {Object.keys(row.mealPreferenceSummary).length > 0 && (
                        <div>
                          <span style={{ font: "var(--type-eyebrow)", display: "block", marginBottom: 4 }}>Meal preferences</span>
                          {Object.entries(row.mealPreferenceSummary).map(([label, count]) => (
                            <span key={label} style={{ font: "var(--type-body-sm)", display: "block" }}>{label}: {count}</span>
                          ))}
                        </div>
                      )}
                      {Object.keys(row.dietarySummary).length > 0 && (
                        <div>
                          <span style={{ font: "var(--type-eyebrow)", display: "block", marginBottom: 4 }}>Dietary needs</span>
                          {Object.entries(row.dietarySummary).map(([label, count]) => (
                            <span key={label} style={{ font: "var(--type-body-sm)", display: "block" }}>{label}: {count}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {row.menu && (
                    <>
                      <DataTable
                        rows={row.menu.courses}
                        emptyLabel="No courses added yet"
                        columns={[
                          { key: "course", header: "Course", width: 140 },
                          { key: "dish", header: "Dish" },
                          { key: "note", header: "Note", muted: true },
                          { key: "actions", header: "", width: 90, render: (course) => (
                            <Button size="sm" variant="danger" disabled={busyCourseId === course.id} onClick={() => removeCourse(row, course.id)}>{busyCourseId === course.id ? "…" : "Remove"}</Button>
                          ) }
                        ]}
                      />
                      <div style={{ marginTop: "var(--space-3)" }}>
                        <Button size="sm" onClick={() => setAddingCourseFor(row)}>Add course</Button>
                      </div>
                    </>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {editingEvent && (
        <MenuSetupDialog
          row={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSaved={(menu) => {
            setOverview((prev) => (prev || []).map((item) => (item.eventId === editingEvent.eventId ? { ...item, menu, cateringGap: menu.cateringCoverCount != null ? Math.max(0, item.confirmedDiners - menu.cateringCoverCount) : 0 } : item)));
            setEditingEvent(null);
          }}
        />
      )}
      {addingCourseFor?.menu && (
        <AddCourseDialog
          menuId={addingCourseFor.menu.id}
          onClose={() => setAddingCourseFor(null)}
          onSaved={(menu) => {
            setOverview((prev) => (prev || []).map((item) => (item.eventId === addingCourseFor.eventId ? { ...item, menu } : item)));
            setAddingCourseFor(null);
          }}
        />
      )}
    </div>
  );
}

function MenuSetupDialog({ row, onClose, onSaved }: { row: EventMenuOverview; onClose: () => void; onSaved: (menu: Menu) => void }) {
  const { service, workspace } = usePlannerData();
  const caterers = workspace!.vendors.filter((v) => v.category.toLowerCase().includes("cater"));
  const [title, setTitle] = React.useState(row.menu?.title || "");
  const [vendorId, setVendorId] = React.useState(row.menu?.vendorId || "");
  const [cateringCoverCount, setCateringCoverCount] = React.useState(row.menu?.cateringCoverCount != null ? String(row.menu.cateringCoverCount) : "");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    const parsedCover = cateringCoverCount.trim() ? Number(cateringCoverCount) : null;
    if (cateringCoverCount.trim() && (!Number.isFinite(parsedCover) || (parsedCover as number) < 0)) {
      setFormError("Catering cover count must be 0 or more.");
      return;
    }
    try {
      const menu = await service.menu.upsertMenu(row.eventId, { title: title.trim() || undefined, vendorId: vendorId || null, cateringCoverCount: parsedCover });
      onSaved(menu);
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title={`Set up menu — ${row.eventName}`}
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Saving…" : "Save"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Menu title"><Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="e.g. Walima dinner" /></Field>
        <Field label="Caterer"><Select value={vendorId} placeholder="No caterer linked" onChange={(event) => setVendorId(event.target.value)} options={caterers.map((v) => ({ value: v.id, label: v.name }))} /></Field>
        <Field label="Catering covers (headcount)" hint="Leave blank if you haven't confirmed a number with your caterer yet.">
          <Input value={cateringCoverCount} type="number" onChange={(event) => setCateringCoverCount(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function AddCourseDialog({ menuId, onClose, onSaved }: { menuId: ID; onClose: () => void; onSaved: (menu: Menu) => void }) {
  const { service } = usePlannerData();
  const [course, setCourse] = React.useState("");
  const [dish, setDish] = React.useState("");
  const [note, setNote] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!course.trim() || !dish.trim()) { setFormError("Give the course a name and a dish."); return; }
    try {
      const menu = await service.menu.addCourse(menuId, course.trim(), dish.trim(), note.trim() || undefined);
      onSaved(menu);
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title="Add course"
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add course"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Course"><Input value={course} onChange={(event) => setCourse(event.target.value)} placeholder="Main" /></Field>
        <Field label="Dish"><Input value={dish} onChange={(event) => setDish(event.target.value)} placeholder="Beef biryani" /></Field>
        <Field label="Note (optional)"><Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Mild spice" /></Field>
      </div>
    </Dialog>
  );
}
